import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Identity and profile — the contract layer for block B06 (`FR-PERM-001`,
 * `FR-PERM-002`).
 *
 * Deliberately narrow. `BE-02.1` — the full Phase 1 object model (workspace,
 * board, list, card, comment, label, membership, notification) — is a
 * different item and stays PENDING. Only what B06 consumes lives here; adding
 * a board schema "while we are in the file" is how that item quietly becomes
 * unreviewable.
 *
 * The file is in two halves and they are not interchangeable: **input
 * primitives**, which validate and normalise what a route accepts, and
 * **profile views**, which describe what a route returns. Reusing an input
 * primitive as a response field is the mistake this file is arranged to
 * prevent — the section comments say why at each boundary.
 *
 * Neither half is a **row contract.** Two of the fields below are not columns:
 *
 *   - `initials` is the EFFECTIVE value, not the column: the nullable
 *     `users.initials` override a platform superadmin set
 *     (`0073_member_initials.sql`, `PATCH /admin/users/:id/initials`) when
 *     there is one, and otherwise derived from the display name at read time
 *     (see `INITIALS_MAX_LENGTH`), so presentation still follows the *current*
 *     name (`FR-PERM-002`) for every member nobody has pinned.
 *   - `emailVerified` is the boolean Better Auth owns. The adjacent
 *     `users.email_verified_at` stamp is derived from it by a trigger, is an
 *     audit timestamp, and is not part of any profile payload.
 *
 * Everything else is a real column in `db/postgres/migrations/0001_identity_and_workspaces.sql`,
 * and every width constant below is that column's width. Getting one wrong is
 * not a type error — it is a `value too long for type character varying(n)`
 * at INSERT time, surfaced to the user as a generic failure, which is exactly
 * the class of mistake this package exists to catch at the boundary.
 */

// ---------------------------------------------------------------------------
// Column widths. Named, exported, and tied one-to-one to the DDL, so a future
// migration that widens a column has exactly one place to change and the
// server's derivation helpers can import the bound instead of restating it.
// ---------------------------------------------------------------------------

/** `users.email varchar(320)` — the RFC 5321 maximum path length. */
export const EMAIL_MAX_LENGTH = 320;

/** `users.username varchar(64)`. */
export const USERNAME_MAX_LENGTH = 64;

/** `users.display_name varchar(128)`. */
export const DISPLAY_NAME_MAX_LENGTH = 128;

/** `users.avatar_url varchar(2048)`. */
export const AVATAR_URL_MAX_LENGTH = 2048;

/** `users.bio varchar(500)` — `FR-PERM-002`'s "optional short bio". */
export const BIO_MAX_LENGTH = 500;

/** `users.initials_colour varchar(32)`. */
export const INITIALS_COLOUR_MAX_LENGTH = 32;

/**
 * The initials cap, and the only number the corpus gives.
 *
 * `FR-PERM-002`'s edge case says initials are "capped at a short length
 * (Trello displays up to four); verify the exact cap for parity". Four it is.
 * Whoever writes the derivation imports this rather than hard-coding a 4 that
 * then disagrees with the renderer's. It is also `users.initials varchar(4)`,
 * the superadmin override — but Postgres counts that width in code points and
 * `publicProfileSchema.initials` counts UTF-16 units, so the override's input
 * schema (`initialsOverrideSchema`) caps in UTF-16 units, the stricter of the
 * two, and anything it accepts serialises on every profile view.
 */
export const INITIALS_MAX_LENGTH = 4;

/**
 * Code points that may serve as an initial: a letter, a number or a symbol.
 *
 * Punctuation, separators, marks and control characters are skipped rather
 * than rendered. `"*** !!!"` is a legal `display_name` — the column has no
 * CHECK beyond NOT NULL — and `"**"` as somebody's avatar is worse than no
 * initials at all. `\p{S}` is in the set on purpose so an emoji name derives
 * the emoji, which is what a member who chose one is asking for.
 *
 * Shared rather than local to the server's `deriveInitials` because the web
 * client's `disambiguateInitials` (`apps/web/src/lib/avatar-labels.ts`) picks
 * the second letter of a clashing label by the same rule — one character
 * class, so the browser can never draw a character the server would refuse.
 * It has no `g` flag on purpose: a global regex carries `lastIndex` between
 * `.test()` calls, and a shared constant would leak that state across callers.
 */
export const INITIAL_CANDIDATE = /[\p{L}\p{N}\p{S}]/u;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
//
// These validate format and they normalise: `.trim()` rewrites its input, and
// `.min(1)` refuses a value the column would technically hold. Both are
// correct at the boundary where a request arrives and wrong anywhere else.
//
// **They are deliberately NOT reused as response fields.** A response schema
// that trims returns a display name the database does not hold — the read path
// then disagrees with the row, silently, and only for members whose stored
// name happens to carry whitespace. A response schema that rejects refuses to
// serve a row that legitimately exists: `users.bio` and `users.avatar_url`
// carry no CHECK constraint, so a fixture, a seed or a row written before a
// patch route existed can hold `''` or a `data:` URL, and `GET /users/:id`
// would 500 on it rather than render it. The profile views below therefore use
// bare width-bounded strings, which assert the one thing that must hold (the
// column width) and change nothing.
//
// The bounds are shared either way, so a width cannot drift between what a
// route accepts and what a read returns — that is what these constants are for.
// ---------------------------------------------------------------------------

/**
 * An email address as `users.email` stores it.
 *
 * The `.max()` is not redundant with the format check: Zod's email validator
 * imposes no length ceiling of its own, and a 400-character address parses
 * happily right up until Postgres rejects it.
 *
 * NOTE for anyone writing a lookup by email: `users.email` is `varchar(320)`
 * with no explicit collation, so Postgres compares it case-sensitively —
 * `Alice@example.com` and `alice@example.com` are two different rows (see
 * `db/postgres/CONVENTIONS.md`, "Expected differences from MySQL"). Better
 * Auth lower-cases on every write and every lookup of its own; hand-written
 * routes must do the same for themselves. This schema deliberately does
 * **not** lower-case, because a schema that silently rewrote its input would
 * make the two paths disagree about what was stored.
 *
 * Nothing in this file parses an email off the wire — Better Auth owns the two
 * endpoints that receive one (see the note at the foot of the file). This is
 * exported for the sign-up boundary and for any later hand-written route that
 * takes an address.
 */
export const emailSchema = z.email().max(EMAIL_MAX_LENGTH);

/**
 * A username as `users.username` stores it.
 *
 * Length only. There is no CHECK constraint on the column and no charset rule
 * anywhere in the corpus, so imposing a regex here would be inventing product
 * behaviour in the contract layer. The derivation that fills this column on
 * sign-up (B06's `databaseHooks.user.create.before`) owns the sanitisation
 * rule — it is the caller of this schema, which is why nothing here parses a
 * username off the wire. `PATCH /me/username` (`BE-44.6`, `FR-NOTIF-033`)
 * changes one through `renameUsernameSchema` below, which is charset-strict
 * where this schema stays length-only: reads describe the row, writes guard
 * the mention contract.
 */
export const usernameSchema = z.string().min(1).max(USERNAME_MAX_LENGTH);

/**
 * A username a member claims for themselves — `PATCH /me/username`,
 * `FR-NOTIF-033` AC3/AC4 (`BE-44.6`).
 *
 * Lower-case only, and that strictness is the point rather than pedantry.
 * `uq_users_username` is case-SENSITIVE under Postgres (one of the port's
 * recorded differences from MySQL's `utf8mb4_0900_ai_ci`), so `Alice` and
 * `alice` would be two legal rows — while `parseMentions` folds every
 * candidate before comparing, so `@Alice` would resolve to BOTH, notifying
 * the wrong person or two people. `deriveUsername` already mints
 * lower-case-only handles for the same reason; this schema keeps renames in
 * the charset sign-up produces, so a claimed handle is always storable,
 * mentionable and unique under the comparison mentions actually perform.
 *
 * The charset mirrors the derivation (`[^a-z0-9._-]`, `profile.ts`): letters,
 * digits, dot, underscore, hyphen. Anything else is a 422, because a handle
 * the mention parser (`@[A-Za-z0-9][A-Za-z0-9._-]*`) cannot address is a
 * handle that can never be notified — claiming it would succeed and then
 * silently never work.
 */
export const renameUsernameSchema = z
  .string()
  .min(1)
  .max(USERNAME_MAX_LENGTH)
  .regex(
    /^[a-z0-9._-]+$/,
    'a username holds lower-case letters, digits, dot, underscore and hyphen only',
  );

export type RenameUsername = z.infer<typeof renameUsernameSchema>;

/**
 * A display name.
 *
 * Trimmed before it is measured, so trailing whitespace cannot smuggle a name
 * past the width bound and a name of nothing but spaces is rejected rather
 * than stored as a blank label on every card the member touches.
 */
export const displayNameSchema = z.string().trim().min(1).max(DISPLAY_NAME_MAX_LENGTH);

/**
 * An avatar URL.
 *
 * Restricted to http/https on purpose. This value ends up as an image `src`,
 * and a bare URL validator accepts `javascript:alert(1)` — a stored-XSS
 * vector handed to us by the profile owner and rendered to everyone who
 * shares a board with them.
 */
export const avatarUrlSchema = z.url({ protocol: /^https?$/ }).max(AVATAR_URL_MAX_LENGTH);

/**
 * A bio.
 *
 * Trimmed and non-empty. **Clearing a bio is `null`, never `''`.** One state,
 * one representation: if both were accepted, "has no bio" would be two
 * different values in the column, and every reader would have to test for
 * both forever. The nullability is applied where the field is used, not here,
 * so the primitive stays composable.
 */
export const bioSchema = z.string().trim().min(1).max(BIO_MAX_LENGTH);

/**
 * A superadmin-set initials override — `users.initials varchar(4)`
 * (`0073_member_initials.sql`), written only by `PATCH /admin/users/:id/initials`.
 *
 * Trimmed, then 1 to `INITIALS_MAX_LENGTH` UTF-16 units (see that constant for
 * why units and not code points), so `''` and a whitespace-only value are both
 * refused rather than stored as an empty circle. No control (`\p{Cc}`) or lone
 * surrogate (`\p{Cs}`) code point: the first renders as nothing, the second
 * cannot be encoded as UTF-8 and would be a 500 at the INSERT.
 *
 * And at least one VISIBLE character (`VISIBLE_INITIAL`): a letter, number,
 * symbol or punctuation code point that is not default-ignorable and is not
 * U+2800 BRAILLE PATTERN BLANK. Trim and the length rule alone admit values
 * that draw an empty circle — measured: U+200B ZERO WIDTH SPACE and U+00AD SOFT
 * HYPHEN (`Cf`), U+FE0F VARIATION SELECTOR-16 (`Mn`), U+3164 HANGUL FILLER
 * (`Lo`, default-ignorable) and U+2800 (`So`, and NOT default-ignorable, hence
 * named). Combining marks are allowed beside a visible base, so a decomposed
 * `É` (E + U+0301) still passes.
 *
 * Kept verbatim otherwise — no upper-casing: the superadmin chose exactly what
 * is drawn.
 */
const VISIBLE_INITIAL = /(?!\p{Default_Ignorable_Code_Point}|⠀)[\p{L}\p{N}\p{S}\p{P}]/u;

export const initialsOverrideSchema = z
  .string()
  .trim()
  .min(1)
  .max(INITIALS_MAX_LENGTH)
  .regex(/^[^\p{Cc}\p{Cs}]+$/u, 'must not contain control characters')
  .regex(VISIBLE_INITIAL, 'must contain at least one visible character');

// ---------------------------------------------------------------------------
// Profile views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms. See the note above
// the input primitives for why: a response schema exists to describe a row, and
// one that rewrites or refuses its input is describing something else.
//
// `id` is the exception and stays `ulidSchema`. A malformed id is not stale
// data a reader should tolerate — it is an unmatchable key, and `eventSchema`
// in `./events` already sets that precedent for the same reason.
// ---------------------------------------------------------------------------

/**
 * The portions of a profile another member may read — `FR-PERM-002`:
 * "Portions of a profile are always visible to anyone who shares a board with
 * the member."
 *
 * This is the *narrow* shape, and it is the base that the owner's view
 * extends. The direction matters: deriving the public view by omitting fields
 * from a private one means every field added later is public by default, and
 * the failure is a leak rather than a compile error.
 *
 * Boards do not exist yet (B08–B10), so B06 serves this to any authenticated
 * user. That scope tightens to shared-board membership without changing this
 * shape.
 */
export const publicProfileSchema = z.object({
  id: ulidSchema,
  username: z.string().max(USERNAME_MAX_LENGTH),
  displayName: z.string().max(DISPLAY_NAME_MAX_LENGTH),
  /**
   * `users.avatar_url` is nullable; `null` means "fall back to initials".
   *
   * Not `avatarUrlSchema`. The http/https restriction is a *write* boundary —
   * it stops a member storing `javascript:` — and the column has no CHECK
   * enforcing it, so a row predating the patch route would make this read
   * throw. Reject on the way in, render what is there on the way out.
   */
  avatarUrl: z.string().max(AVATAR_URL_MAX_LENGTH).nullable(),
  /**
   * The superadmin's override (`users.initials`) when set, otherwise derived
   * from the display name; up to `INITIALS_MAX_LENGTH`. No lower bound: a
   * display name made entirely of punctuation or emoji derives no initials at
   * all, and that is a presentation problem for the renderer, not a reason to
   * reject the row.
   */
  initials: z.string().max(INITIALS_MAX_LENGTH),
  /**
   * The stable per-account colour behind those initials (`FR-PERM-002` AC-1).
   *
   * No `.min(1)`: the column is `NOT NULL DEFAULT ''`, so any row not created
   * through the sign-up hook — a fixture, a seed, a hand-inserted test row —
   * legitimately holds the empty string, and a lower bound here would make
   * this schema reject real data.
   */
  initialsColour: z.string().max(INITIALS_COLOUR_MAX_LENGTH),
  /**
   * Not `bioSchema`. `bioSchema.min(1)` is the rule that keeps "has no bio"
   * to one representation *on the way in*; nothing stops an existing row
   * holding `''`, and refusing to serve that row would be a 500 on a read.
   */
  bio: z.string().max(BIO_MAX_LENGTH).nullable(),
});

export type PublicProfile = z.infer<typeof publicProfileSchema>;

/**
 * The authenticated user's own view of themselves — what `GET /me` returns.
 *
 * Exactly the public profile plus the two facts that belong to the account
 * rather than to the presentation of it. Nobody else ever sees these.
 */
export const ownProfileSchema = publicProfileSchema.extend({
  /** Bounded, not format-checked — see the note above. `emailSchema` is input. */
  email: z.string().max(EMAIL_MAX_LENGTH),
  /**
   * Better Auth's boolean. `FR-NOTIF-010` gates notification email on it.
   * The `users.email_verified_at` stamp is derived from this by a trigger and
   * is not part of the payload.
   */
  emailVerified: z.boolean(),
});

export type OwnProfile = z.infer<typeof ownProfileSchema>;

/**
 * What `GET /me` returns — and ONLY `GET /me`: the owner's view plus whether the
 * account is a platform superadmin (`docs/notes/superadmin-requirements.md`,
 * SA-3 and SA-9). The web reads it to decide whether to offer the admin console
 * at all; the server never trusts it back.
 *
 * ⚠ A COPY, NOT A WIDENING. `ownProfileSchema` also serialises the three
 * `PATCH /me/*` responses and the username rename, and none of them has any
 * business carrying the flag. Widening it in place would put `isSuper` on every
 * one of those bodies with nothing red anywhere; extending a copy leaves them
 * exactly as they were.
 *
 * ⚠ THE ROUTE MUST POINT ITS `response` AT THIS SCHEMA. Fastify's serialiser
 * strips an undeclared key, so a handler that returned `isSuper` against
 * `ownProfileSchema` would put nothing on the wire, and every client gate would
 * read `false` for a real superadmin — again with nothing red.
 *
 * `.default(false)` is for a reader, never for the writer: a client parsing an
 * older server's body gets `false` rather than a parse failure, and a missing
 * key fails closed. The server handler supplies the value explicitly, typed
 * against `SessionProfile` (the *output* type, where the key is required), so
 * it cannot fall back to the default by omission.
 *
 * Not a `strictObject`: it extends a plain `z.object`, like the shape it copies.
 */
export const sessionProfileSchema = ownProfileSchema.extend({
  isSuper: z.boolean().default(false),
});

export type SessionProfile = z.infer<typeof sessionProfileSchema>;

// ---------------------------------------------------------------------------
// Field-scoped patch bodies — SC-4
//
// One schema per field, never one object schema covering the profile. SC-4 is
// not a stylistic preference: `FR-EXP-031` specifies last-write-wins *per
// field* and `FR-COLLAB-026` forbids one member's edit clobbering another's
// in-progress draft on a different field. A single `PUT /me` cannot express
// either, because an absent key and a cleared value are indistinguishable in
// the body it receives.
//
// Every one of these is a `strictObject`. A plain `z.object()` in Zod 4
// *strips* unknown keys rather than rejecting them, which would let
// `PATCH /me/bio` quietly swallow `{ bio, username }` and return 200 having
// ignored half the request — the caller believes the username changed. The
// strict form makes that a 422 with the offending key named.
// ---------------------------------------------------------------------------

/** `PATCH /me/display-name`. Never nullable — `users.display_name` is NOT NULL. */
export const displayNamePatchSchema = z.strictObject({
  displayName: displayNameSchema,
});

export type DisplayNamePatch = z.infer<typeof displayNamePatchSchema>;

/** `PATCH /me/bio`. `null` clears it; `''` is rejected — see `bioSchema`. */
export const bioPatchSchema = z.strictObject({
  bio: bioSchema.nullable(),
});

export type BioPatch = z.infer<typeof bioPatchSchema>;

/** `PATCH /me/avatar`. `null` removes the image and falls back to initials. */
export const avatarPatchSchema = z.strictObject({
  avatarUrl: avatarUrlSchema.nullable(),
});

export type AvatarPatch = z.infer<typeof avatarPatchSchema>;

/**
 * `PATCH /me/username` — `FR-NOTIF-033` AC3 (`BE-44.6`). Never nullable:
 * an account without a handle cannot be mentioned, so clearing is refused
 * and there is no second representation of "has no username".
 */
export const usernamePatchSchema = z.strictObject({
  username: renameUsernameSchema,
});

export type UsernamePatch = z.infer<typeof usernamePatchSchema>;

/**
 * `GET /users/username-availability` — `FR-NOTIF-033` AC4 (`BE-44.6`).
 *
 * The query carries the SAME charset rule as the rename, and an
 * unclaimable handle is a 422 here too rather than an `available: true`
 * that the rename would then refuse: "available" answers "nobody holds
 * this", and a handle the parser cannot address is a different refusal
 * that must not wear its shape.
 */
export const usernameAvailabilityQuerySchema = z.strictObject({
  username: renameUsernameSchema,
});

export type UsernameAvailabilityQuery = z.infer<typeof usernameAvailabilityQuerySchema>;

/** What the availability read answers: the handle asked about, and whether claiming it would succeed. */
export const usernameAvailabilitySchema = z.strictObject({
  username: renameUsernameSchema,
  available: z.boolean(),
});

export type UsernameAvailability = z.infer<typeof usernameAvailabilitySchema>;

// ---------------------------------------------------------------------------
// The avatar UPLOAD contract — `FR-NOTIF-033` AC1, and the half of `FE-44.2`
// that had no route.
//
// ⚠ **These describe an upload, not a second way to write `avatar_url`.**
// `avatarPatchSchema` above stays the ONLY write path for the column: the
// presign route below mints the durable URL the object will be served at, the
// client PUTs the bytes straight to storage (SC-3 — the Node process never
// sees them), and the client then sends that URL through the shipped
// `PATCH /me/avatar`. So there is no confirm route and no second validation
// boundary: `avatarUrlSchema`'s http/https restriction, which exists to stop
// `javascript:` reaching an `<img src>`, still guards every value that reaches
// the column.
//
// ⚠ **Nothing here widens `publicProfileSchema` or `ownProfileSchema`, and
// that is measured rather than assumed**: 73 files reference the first and 25
// the second, and `toPublicProfile` is written out at five construction sites.
// The uploaded avatar needs no new field anywhere, because the URL it produces
// *is* an `avatar_url` value and travels in the field that already exists.
// ---------------------------------------------------------------------------

/**
 * The ceiling on an uploaded avatar, in bytes.
 *
 * Not a column width and not a corpus number — `FR-NOTIF-033` gives no size,
 * and `FR-CARD-022`'s 250 MB is an *attachment* limit whose whole point is
 * that a member may attach a video. An avatar is rendered at 24–40 px and is
 * fetched by every viewer of every board the member appears on, so the number
 * that matters is the one that keeps a board readable rather than the one the
 * storage layer could survive. Ten megabytes is generous for that and small
 * enough that a mistake is cheap.
 *
 * ⚠ It is enforced at the **storage** layer, not merely here: the presign
 * route passes `sizeBytes` to `presignUpload` as `ContentLength`, which is a
 * SIGNED header — a client that PUTs a larger body gets `SignatureDoesNotMatch`
 * and no object is written. `apps/server/src/storage/s3.ts`'s header measures
 * that; this constant is the policy it signs.
 */
export const AVATAR_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/**
 * What an avatar may be uploaded as.
 *
 * The same four `apps/server/src/routes/board-background.ts` accepts, and
 * deliberately not a superset: every one of them is a raster format a browser
 * decodes in an `<img>` without a plugin. **SVG is excluded on purpose** — an
 * SVG is a document that can carry script, and this file ends up rendered
 * under our own origin, which is exactly the stored-XSS shape `avatarUrlSchema`
 * refuses `javascript:` for one section up.
 */
export const AVATAR_UPLOAD_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

/** `avatars/{userId}/{avatarId}` — comfortably inside this, with room to spare. */
export const AVATAR_STORAGE_KEY_MAX_LENGTH = 512;

export const avatarUploadContentTypeSchema = z.enum(AVATAR_UPLOAD_CONTENT_TYPES);

export type AvatarUploadContentType = z.infer<typeof avatarUploadContentTypeSchema>;

/**
 * `POST /me/avatar/presign`.
 *
 * ⚠ **No `fileName`, unlike the attachment and board-background presigns.**
 * Both of those need one because their object key carries the uploaded file's
 * extension and their row carries a display name a member later renames. An
 * avatar has neither: its key is `avatars/{userId}/{avatarId}` with **no
 * extension** (the content type is the object's own metadata, and the presigned
 * `GET` hands it back), and `users.avatar_url` holds a URL rather than a name.
 * A `fileName` here would be a field nothing reads — dead weight in a contract
 * that a later reader would try to make load-bearing.
 *
 * `strictObject`: a plain `z.object()` in Zod 4 *strips* unknown keys, so a
 * caller sending `{ contentType, sizeBytes, avatarUrl }` would get a 200 and a
 * signed URL while believing it had also set the column. The strict form names
 * the offending key in a 422 instead.
 */
export const avatarUploadPresignBodySchema = z.strictObject({
  contentType: avatarUploadContentTypeSchema,
  sizeBytes: z.number().int().positive().max(AVATAR_UPLOAD_MAX_BYTES),
});

export type AvatarUploadPresignBody = z.infer<typeof avatarUploadPresignBodySchema>;

/**
 * What `POST /me/avatar/presign` returns.
 *
 * `avatarUrl` is the point of the whole exchange: the **durable, absolute** URL
 * the uploaded object will be served at, built server-side from `WEB_ORIGIN`
 * (the idiom `canonicalCardUrl` in `apps/server/src/routes/card-share.ts` and
 * the notification email renderer already use). The client never constructs it
 * and never has to know the storage layout.
 *
 * ⚠ **It is not the presigned `GET`.** A presigned `GET` expires in about a
 * minute, so a column that stored one would hold a broken image by the time
 * anybody loaded the page. This URL points at our own API, which checks the
 * request and redirects to a freshly signed `GET` each time — the shape
 * `GET /attachments/:id/download` already ships.
 *
 * `headers` are the exact headers the `PUT` must carry. A presigned URL signed
 * over headers rejects a request that omits or alters either with a signature
 * error rather than a helpful one, so they are handed back rather than left to
 * be inferred.
 */
export const avatarUploadPresignSchema = z.strictObject({
  storageKey: z.string().min(1).max(AVATAR_STORAGE_KEY_MAX_LENGTH),
  uploadUrl: z.url(),
  headers: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int().positive(),
  /** Bounded by the column this value is destined for. */
  avatarUrl: z.url({ protocol: /^https?$/ }).max(AVATAR_URL_MAX_LENGTH),
  acceptedContentTypes: z.array(avatarUploadContentTypeSchema).min(1),
  maxSizeBytes: z.literal(AVATAR_UPLOAD_MAX_BYTES),
});

export type AvatarUploadPresign = z.infer<typeof avatarUploadPresignSchema>;

// ---------------------------------------------------------------------------
// The username write surface — `FR-NOTIF-033` AC3/AC4, built by `BE-44.6`.
//
// This block used to record three measured reasons the rename could not ship
// inside a contract chunk (no `user.username_changed` in either live CHECK,
// no table for a former username, no caller for an availability check).
// All three are now closed: `0059_username_history` carries the table and
// both widenings, `PATCH /me/username` (`usernamePatchSchema` above) records
// the rename, `GET /users/username-availability`
// (`usernameAvailabilityQuerySchema` / `usernameAvailabilitySchema` above)
// answers the check, and both mention resolvers read the history.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sign-in and sign-up bodies are deliberately ABSENT.
//
// Better Auth owns `/auth/sign-in/email` and `/auth/sign-up/email` and
// validates their bodies itself (`parseInputData` raises `MISSING_FIELD`;
// a duplicate address is caught pre-insert and returned as
// `USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL`). Declaring a `signUpBodySchema`
// here that no route ever parses would be dead code and a second source of
// truth for a wire shape this repository does not own — the two would drift
// on the first Better Auth upgrade and nothing would fail.
//
// There is one real gap and this is where a reader would ask about it: Better
// Auth does not know that `name` lands in `display_name varchar(128)`, so it
// accepts a 300-character name that Postgres then refuses. The fix is
// `displayNameSchema` above, applied at the sign-up boundary by B06's wiring
// — not a duplicate body schema.
// ---------------------------------------------------------------------------
